import { useState } from 'react';
import { X } from 'lucide-react';
import { Tag } from '../../types';
import './TagPickerSection.css';

interface TagPickerSectionProps {
  selectedTagIds: number[];
  availableTags: Tag[];
  onTagAdd: (tagId: number) => void;
  onTagRemove: (tagId: number) => void;
  onCreateTag: (name: string, type: 'person' | 'place' | 'year' | 'event' | 'custom') => void;
  isLoading?: boolean;
}

export default function TagPickerSection({
  selectedTagIds,
  availableTags,
  onTagAdd,
  onTagRemove,
  onCreateTag,
  isLoading = false,
}: TagPickerSectionProps) {
  const [searchText, setSearchText] = useState('');

  // Filter available tags by search and exclude already selected
  const filteredTags = availableTags.filter(
    tag =>
      !selectedTagIds.includes(tag.id) &&
      tag.name.toLowerCase().includes(searchText.toLowerCase())
  );

  // Get selected tags for display
  const selectedTags = availableTags.filter(tag =>
    selectedTagIds.includes(tag.id)
  );

  return (
    <div className="tag-picker-section">
      <div className="tag-picker-label">Tags (optional)</div>

      {/* Selected tags display */}
      {selectedTags.length > 0 && (
        <div className="tag-picker-selected">
          <div className="tag-picker-selected-label">
            Applied to all photos ({selectedTags.length})
          </div>
          <div className="tag-picker-chip-container">
            {selectedTags.map(tag => (
              <div
                key={tag.id}
                className="tag-picker-chip"
                style={{
                  background: tag.color ? `${tag.color}30` : undefined,
                  border: `1px solid ${tag.color || 'var(--border-color)'}`,
                }}
              >
                {tag.type === 'person' && '👤'}
                {tag.type === 'place' && '📍'}
                {tag.type === 'year' && '📅'}
                {tag.type === 'event' && '🎉'}
                {tag.type === 'custom' && '🏷️'}
                <span>{tag.name}</span>
                <button
                  onClick={() => onTagRemove(tag.id)}
                  disabled={isLoading}
                  className="tag-picker-chip-remove"
                  title="Remove tag"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Search input */}
      <input
        type="text"
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        placeholder="Search or create tags..."
        disabled={isLoading}
        className="tag-picker-input"
      />

      {/* Available tags to add */}
      {searchText && filteredTags.length > 0 && (
        <div className="tag-picker-suggestions">
          <div className="tag-picker-suggestions-label">Add existing:</div>
          <div className="tag-picker-button-group">
            {filteredTags.slice(0, 10).map(tag => (
              <button
                key={tag.id}
                onClick={() => {
                  onTagAdd(tag.id);
                  setSearchText('');
                }}
                disabled={isLoading}
                className="tag-picker-suggestion-btn"
                style={{
                  background: tag.color ? `${tag.color}20` : undefined,
                  border: tag.color ? `1px solid ${tag.color}` : undefined,
                }}
              >
                {tag.type === 'person' && '👤'}
                {tag.type === 'place' && '📍'}
                {tag.type === 'year' && '📅'}
                {tag.type === 'event' && '🎉'}
                {tag.type === 'custom' && '🏷️'}
                <span>{tag.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Create new tag */}
      {searchText && filteredTags.length === 0 && (
        <div className="tag-picker-create">
          <div className="tag-picker-create-label">
            Create "{searchText}" as:
          </div>
          <div className="tag-picker-button-group">
            {(['person', 'place', 'year', 'event', 'custom'] as const).map(
              type => (
                <button
                  key={type}
                  onClick={() => {
                    onCreateTag(searchText, type);
                    setSearchText('');
                  }}
                  disabled={isLoading}
                  className="tag-picker-create-btn"
                >
                  {type === 'person' && '👤'}
                  {type === 'place' && '📍'}
                  {type === 'year' && '📅'}
                  {type === 'event' && '🎉'}
                  {type === 'custom' && '🏷️'}
                  <span>{type}</span>
                </button>
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
}
